export interface AuditLogFilters {
  organisationId: string;
  actor: string;
  target: string;
  action: string;
  fromDate: string;
  toDate: string;
}

export type AuditLogPageViewState = "loading" | "error" | "empty" | "ready";
export type AuditLogFilterName = keyof AuditLogFilters;
export type AuditLogFilterValueKind =
  | "datetime_like"
  | "email_like"
  | "event_name_like"
  | "identifier_like"
  | "text_like"
  | "uuid_like";

const AUDIT_LOG_FILTER_NAMES: AuditLogFilterName[] = [
  "organisationId",
  "actor",
  "target",
  "action",
  "fromDate",
  "toDate",
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const ISO_DATE_PATTERN =
  /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:\d{2})?)?$/;
const EVENT_NAME_PATTERN = /^[a-z0-9]+(?:[._:-][a-z0-9]+)+$/i;
const IDENTIFIER_PATTERN = /^[a-z0-9][a-z0-9._:-]{2,}$/i;

const trimToOptional = (value: string): string | undefined => {
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : undefined;
};

const classifyAuditLogFilterValue = (
  filter: AuditLogFilterName,
  normalizedValue: string,
): AuditLogFilterValueKind => {
  if (UUID_PATTERN.test(normalizedValue)) {
    return "uuid_like";
  }

  if (filter === "actor" && EMAIL_PATTERN.test(normalizedValue)) {
    return "email_like";
  }

  if (
    (filter === "fromDate" || filter === "toDate") &&
    ISO_DATE_PATTERN.test(normalizedValue)
  ) {
    return "datetime_like";
  }

  if (filter === "action" && EVENT_NAME_PATTERN.test(normalizedValue)) {
    return "event_name_like";
  }

  if (IDENTIFIER_PATTERN.test(normalizedValue)) {
    return "identifier_like";
  }

  return "text_like";
};

export const buildAuditLogFilterTelemetryContext = (
  filters: AuditLogFilters,
): {
  activeFilterCount: number;
  activeFilterNames: AuditLogFilterName[];
  filterValueKinds: Partial<
    Record<AuditLogFilterName, AuditLogFilterValueKind>
  >;
  hasActionFilter: boolean;
  hasActorFilter: boolean;
  hasFromDateFilter: boolean;
  hasOrganisationIdFilter: boolean;
  hasTargetFilter: boolean;
  hasToDateFilter: boolean;
  queryScope: "global" | "organisation";
} => {
  const activeFilterNames: AuditLogFilterName[] = [];
  const filterValueKinds: Partial<
    Record<AuditLogFilterName, AuditLogFilterValueKind>
  > = {};

  for (const filter of AUDIT_LOG_FILTER_NAMES) {
    const normalizedValue = trimToOptional(filters[filter]);
    if (!normalizedValue) {
      continue;
    }

    activeFilterNames.push(filter);
    filterValueKinds[filter] = classifyAuditLogFilterValue(
      filter,
      normalizedValue,
    );
  }

  return {
    activeFilterCount: activeFilterNames.length,
    activeFilterNames,
    filterValueKinds,
    hasActionFilter: activeFilterNames.includes("action"),
    hasActorFilter: activeFilterNames.includes("actor"),
    hasFromDateFilter: activeFilterNames.includes("fromDate"),
    hasOrganisationIdFilter: activeFilterNames.includes("organisationId"),
    hasTargetFilter: activeFilterNames.includes("target"),
    hasToDateFilter: activeFilterNames.includes("toDate"),
    queryScope: activeFilterNames.includes("organisationId")
      ? "organisation"
      : "global",
  };
};

export const buildAuditLogChangedFilterTelemetryContext = (
  filter: AuditLogFilterName,
  value: string,
): {
  changedFilter: AuditLogFilterName;
  changedValueKind: AuditLogFilterValueKind | "empty";
  changedValuePresent: boolean;
} => {
  const normalizedValue = trimToOptional(value);

  return {
    changedFilter: filter,
    changedValueKind: normalizedValue
      ? classifyAuditLogFilterValue(filter, normalizedValue)
      : "empty",
    changedValuePresent: normalizedValue !== undefined,
  };
};

export const resolveAuditLogPageViewState = (args: {
  isLoading: boolean;
  isError: boolean;
  entryCount: number;
}): AuditLogPageViewState => {
  if (args.isLoading) {
    return "loading";
  }

  if (args.isError) {
    return "error";
  }

  if (args.entryCount === 0) {
    return "empty";
  }

  return "ready";
};
